import { apiSlice } from '../app/api/apiSlice';

export const ordersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getOrders: builder.query({
      query: ({ page = 1, limit = 10, search = '', status = '' } = {}) => {
        const params = new URLSearchParams();
        if (page) params.append('page', page);
        if (limit) params.append('limit', limit);
        if (search) params.append('search', search);
        if (status) params.append('status', status);

        return {
          url: `orders${params.toString() ? `?${params.toString()}` : ''}`,
        };
      },
      validateStatus: (response, result) => {
        console.log(result);
        return response.status === 200 && !result.isError;
      },
    }),
    orderDetails: builder.mutation({
      query: (payload) => ({
        url: `order/details`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    addStatus: builder.mutation({
      query: (payload) => ({
        url: `order/status`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    getIngredientPrices: builder.query({
      query: (lang) => ({ url: `ingredientPrices?lang=${lang}` }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    getIngredientsCodes: builder.query({
      query: () => ({ url: `ingredient/allIngredients` }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    getIngredientsList: builder.query({
      query: () => ({ url: `ingredient/all` }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    createLabel: builder.mutation({
      query: (payload) => ({
        url: `order/shipment`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    validateLabel: builder.mutation({
      query: (payload) => ({
        url: `order/validate-dhl-shipments`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    createExternalLabel: builder.mutation({
      query: (payload) => ({
        url: `external-order/shipment`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    validateExternalLabel: builder.mutation({
      query: (payload) => ({
        url: `external-order/validate-dhl-shipments`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    updateBulkOrderStatuses: builder.mutation({
      query: (payload) => ({
        url: `order/bulk-status-update`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    updateBulkExternalOrderStatuses: builder.mutation({
      query: (payload) => ({
        url: `external-order/bulk-status-update`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    getShipmentTracking: builder.query({
      query: (trackingIds) => ({ url: `order/dhl-shipments?trackingIds=${trackingIds}` }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    updateBulkOrderStatusesFromDhl: builder.mutation({
      query: (payload) => ({
        url: `order/bulk-status-update-dhl`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    getExternalOrdersShipmentTracking: builder.query({
      query: (trackingIds) => ({ url: `external-order/dhl-shipments?trackingIds=${trackingIds}` }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    updateExternalOrdersBulkOrderStatusesFromDhl: builder.mutation({
      query: (payload) => ({
        url: `external-order/bulk-status-update-dhl`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    sendEmailUpdates: builder.mutation({
      query: (payload) => ({
        url: `order/send-email-update`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    sendExternalOrderEmailUpdates: builder.mutation({
      query: (payload) => ({
        url: `external-order/email-shipment-updates`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    updateOrderStatusAndDetails: builder.mutation({
      query: (payload) => ({
        url: `order/update-status-and-details`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    assignBatch: builder.mutation({
      query: (payload) => ({
        url: `order/batch`,
        method: 'PUT',
        body: { ...payload },
      }),
    }),
    getIngredientsList: builder.query({
      query: () => ({ url: `ingredient/all` }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    getFormulaByReference: builder.query({
      query: ({ orderId, quizId }) => {
        const params = new URLSearchParams();
        if (orderId) params.append('orderId', orderId);
        if (quizId) params.append('quizId', quizId);

        return {
          url: `admin/formula${params.toString() ? `?${params.toString()}` : ''}`,
        };
      },
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
    }),
    saveAdminFormula: builder.mutation({
      query: (payload) => ({
        url: `admin/formula/save`,
        method: 'POST',
        body: { ...payload },
      }),
    }),
    quizLookup: builder.query({
      query: (quizId) => ({ url: `admin/quiz-lookup/${quizId}` }),
    }),
    quizDetails: builder.query({
      query: (customerQuizId) => ({ url: `admin/quiz-details/${customerQuizId}` }),
    }),
    getOperators: builder.query({
      query: () => ({ url: `ops/operators` }),
    }),
    createOperator: builder.mutation({
      query: (payload) => ({
        url: `ops/operator`,
        method: 'POST',
        body: payload,
      }),
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useOrderDetailsMutation,
  useAddStatusMutation,
  useGetIngredientPricesQuery,
  useGetIngredientsCodesQuery,
  useCreateLabelMutation,
  useValidateLabelMutation,
  useCreateExternalLabelMutation,
  useValidateExternalLabelMutation,
  useUpdateBulkOrderStatusesMutation,
  useUpdateBulkExternalOrderStatusesMutation,
  useLazyGetShipmentTrackingQuery,
  useUpdateBulkOrderStatusesFromDhlMutation,
  useLazyGetExternalOrdersShipmentTrackingQuery,
  useUpdateExternalOrdersBulkOrderStatusesFromDhlMutation,
  useSendEmailUpdatesMutation,
  useSendExternalOrderEmailUpdatesMutation,
  useUpdateOrderStatusAndDetailsMutation,
  useAssignBatchMutation,
  useGetIngredientsListQuery,
  useLazyGetFormulaByReferenceQuery,
  useSaveAdminFormulaMutation,
  useLazyQuizLookupQuery,
  useLazyQuizDetailsQuery,
  useGetOperatorsQuery,
  useCreateOperatorMutation,
} = ordersApiSlice;
